import type { ReactNode } from "react";
import { filterGenres, filterRegions, filterYears, sortOptions } from "@/mocks/site";

interface FilterPanelProps {
  type: string;
  selectedGenres: string[];
  selectedRegions: string[];
  selectedYears: number[];
  sort: string;
  onToggleGenre: (v: string) => void;
  onToggleRegion: (v: string) => void;
  onToggleYear: (v: number) => void;
  onSort: (v: string) => void;
  onReset: () => void;
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2 border-b border-background-200/50 py-4 last:border-0 sm:flex-row sm:items-start sm:gap-4">
      <span className="shrink-0 pt-1 text-sm text-foreground-300 sm:w-16">{label}</span>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-3.5 py-1.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
        active
          ? "bg-primary-500 text-background-50"
          : "bg-background-100 text-foreground-400 hover:text-foreground-950"
      }`}
    >
      {children}
    </button>
  );
}

export default function FilterPanel({
  type,
  selectedGenres,
  selectedRegions,
  selectedYears,
  sort,
  onToggleGenre,
  onToggleRegion,
  onToggleYear,
  onSort,
  onReset,
}: FilterPanelProps) {
  const hasFilter =
    selectedGenres.length > 0 || selectedRegions.length > 0 || selectedYears.length > 0;

  return (
    <div className="rounded-lg border border-background-200/60 bg-background-100/40 p-4 md:p-5">
      <Row label="类型">
        {(filterGenres[type] || []).map((g) => (
          <Chip key={g} active={selectedGenres.includes(g)} onClick={() => onToggleGenre(g)}>
            {g}
          </Chip>
        ))}
      </Row>
      <Row label="地区">
        {filterRegions.map((r) => (
          <Chip key={r} active={selectedRegions.includes(r)} onClick={() => onToggleRegion(r)}>
            {r}
          </Chip>
        ))}
      </Row>
      <Row label="年份">
        {filterYears.map((y) => (
          <Chip key={y} active={selectedYears.includes(y)} onClick={() => onToggleYear(y)}>
            {y}
          </Chip>
        ))}
      </Row>
      <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-foreground-300">排序</span>
          {sortOptions.map((o) => (
            <button
              key={o.key}
              type="button"
              onClick={() => onSort(o.key)}
              className={`rounded-md px-3 py-1.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                sort === o.key
                  ? "bg-accent-500 text-background-50"
                  : "bg-background-100 text-foreground-400 hover:text-foreground-950"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={onReset}
          disabled={!hasFilter}
          className={`flex items-center gap-1.5 rounded-md border px-4 py-2 text-sm transition-colors whitespace-nowrap ${
            hasFilter
              ? "border-background-300/70 text-foreground-500 hover:border-accent-500 hover:text-accent-500 cursor-pointer"
              : "border-background-200/50 text-foreground-200 cursor-not-allowed"
          }`}
        >
          <i className="ri-refresh-line"></i>
          重置筛选
        </button>
      </div>
    </div>
  );
}